import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, EyeOff, Mail, Lock, AlertCircle, X } from 'lucide-react';
import LoginImg from "../assets/Login Cover Image.png";
import { loginUser, clearError } from "../../redux/slices/authSlice";
import { mergeCart } from '../../redux/slices/cartSlice';

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showError, setShowError] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, guestId, loading, error } = useSelector((state) => state.auth);
    const { cart } = useSelector((state) => state.cart);

    // Get redirect parameter
    const redirect = new URLSearchParams(location.search).get("redirect") || "/";
    const isCheckoutRedirect = redirect.includes("checkout");

    // Show error alert when error occurs
    useEffect(() => {
        if (error) {
            setShowError(true);
            // Auto-hide error after 5 seconds
            const timer = setTimeout(() => {
                setShowError(false);
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (user) {
            if (cart?.products.length > 0 && guestId) {
                dispatch(mergeCart({ guestId, user })).then(() => {
                    navigate(isCheckoutRedirect ? "/checkout" : "/");
                });
            } else {
                navigate(isCheckoutRedirect ? "/checkout" : "/");
            }
        }
    }, [user, guestId, cart, navigate, isCheckoutRedirect, dispatch]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowError(false);
        dispatch(loginUser({ email, password }));
    };

    const dismissError = () => {
        setShowError(false);
        dispatch(clearError());
    };

    return (
        <div className="flex min-h-screen">
            {/* Left Side - Login Form */}
            <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-12 bg-gray-50">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img 
                            src="https://res.cloudinary.com/dukimifv5/image/upload/t_For_Website/logo1_xqvdhl" 
                            className="h-16 w-auto object-contain"
                            alt="VESHH-EON Logo"
                        />
                    </div>

                    {/* Form Container */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-10">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                Welcome Back!
                            </h2>
                            <p className="text-gray-600">
                                Please enter your credentials to continue
                            </p>
                        </div>

                        {/* Error Alert */}
                        {showError && error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3 animate-shake">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-red-800 text-sm font-medium">
                                        {error === "Invalid credentials" || error === "Invalid email or password" 
                                            ? "Invalid email or password. Please try again."
                                            : error === "User not found"
                                            ? "No account found with this email address."
                                            : error || "Login failed. Please try again."}
                                    </p>
                                </div>
                                <button 
                                    onClick={dismissError}
                                    className="text-red-400 hover:text-red-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        )}

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email Field */}
                            <div>
                                <label 
                                    className="block text-gray-700 text-sm font-semibold mb-2" 
                                    htmlFor="email"
                                >
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                            showError && error 
                                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' 
                                                : 'border-gray-200 focus:border-black focus:ring-black/10'
                                        }`}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label 
                                    className="block text-gray-700 text-sm font-semibold mb-2" 
                                    htmlFor="password"
                                >
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                            showError && error 
                                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' 
                                                : 'border-gray-200 focus:border-black focus:ring-black/10'
                                        }`}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Forgot Password Link */}
                            <div className="flex items-center justify-end">
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-gray-600 hover:text-black transition-colors"
                                >
                                    Forgot Password?
                                </Link>
                            </div>

                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                disabled={loading}
                                className="w-full bg-black text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-black/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Signing In...
                                    </span>
                                ) : (
                                    "Sign In"
                                )}
                            </button>
                        </form>

                        {/* Register Link */}
                        <p className="mt-8 text-center text-sm text-gray-600">
                            Don't have an account?{' '}
                            <Link 
                                to={`/register?redirect=${encodeURIComponent(redirect)}`} 
                                className="font-semibold text-black hover:underline transition-all"
                            >
                                Create Account
                            </Link>
                        </p>
                    </div>

                    {/* Trust Indicators */}
                    <div className="mt-8 text-center">
                        <p className="text-xs text-gray-500">
                            By signing in, you agree to our{' '}
                            <Link to="/terms" className="underline hover:text-gray-700">Terms</Link>
                            {' '}and{' '}
                            <Link to="/privacypolicy" className="underline hover:text-gray-700">Privacy Policy</Link>
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Side - Image */}
            <div className="hidden md:flex w-1/2 bg-black relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-transparent z-10"></div>
                <img 
                    src={LoginImg} 
                    alt="Login Visual" 
                    className="w-full h-full object-cover"
                />
                <div className="absolute bottom-12 left-12 z-20 text-white max-w-md">
                    <h3 className="text-3xl font-bold mb-4">
                        Experience Fashion, Redefined
                    </h3>
                    <p className="text-gray-200 text-lg">
                        Join thousands of fashion enthusiasts who trust VESHH-EON for their style needs.
                    </p>
                </div>
            </div>

            {/* CSS for shake animation */}
            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default Login;
